import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { IFilterPresetRuntime } from "@models/IFilterPresetsUI";
import { OwSidebar } from "../OwSidebar/OwSidebar";
import { IOwSortableListItem, OwSortableList } from "../OwSortableList/OwSortableList";

type Props = {
    isOpen: boolean;
    presets: IFilterPresetRuntime[];
    activePresetId: string | null;
    translate: (id: string, values?: { [key: string]: string | number }) => string;
    onReorder: (source: "team" | "user", orderedIds: string[]) => void;
    onRename: (presetId: string, name: string) => void;
    onDelete: (presetId: string) => void;
    onToggleDefault: (presetId: string) => void;
    onClose: () => void;
};

type RowProps = {
    preset: IFilterPresetRuntime;
    isActive: boolean;
    translate: Props["translate"];
    onRename: (name: string) => void;
    onDelete: () => void;
    onToggleDefault: () => void;
};

/** One preset row: name (click the pencil to rename inline), default star, delete. */
const PresetRow = (props: RowProps) => {
    const { preset, translate } = props;
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(preset.name);

    useEffect(() => {
        if (!editing) {
            setName(preset.name);
        }
    }, [preset.name, editing]);

    const commit = () => {
        setEditing(false);

        if (name.trim().length && name.trim() !== preset.name) {
            props.onRename(name);
        } else {
            setName(preset.name);
        }
    };

    const cancel = () => {
        setEditing(false);
        setName(preset.name);
    };

    if (editing) {
        return (
            <div className={"FilterBar-manage-row d-flex align-items-center"}>
                <input
                    className={"form-control form-control-sm"}
                    aria-label={translate("preset_name")}
                    autoFocus={true}
                    value={name}
                    maxLength={60}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            commit();
                        } else if (e.key === "Escape") {
                            e.preventDefault();
                            cancel();
                        }
                    }}
                />
            </div>
        );
    }

    return (
        <div className={"FilterBar-manage-row d-flex align-items-center"}>
            <span className={"FilterBar-manage-name flex-grow-1 text-truncate" + (props.isActive ? " active" : "")} title={preset.name}>
                {preset.isDefault ? <i className={"fas fa-star me-1 FilterBar-default-star"} title={translate("is_default_view")} /> : null}
                {preset.name}
            </span>
            {preset.editable ? (
                <>
                    <button
                        type={"button"}
                        className={"btn btn-sm btn-link FilterBar-manage-action"}
                        title={translate(preset.isDefault ? "clear_default" : "set_as_default")}
                        aria-label={`${translate(preset.isDefault ? "clear_default" : "set_as_default")} ${preset.name}`}
                        onClick={props.onToggleDefault}>
                        <i className={preset.isDefault ? "fas fa-star" : "far fa-star"} />
                    </button>
                    <button
                        type={"button"}
                        className={"btn btn-sm btn-link FilterBar-manage-action"}
                        title={translate("rename")}
                        aria-label={`${translate("rename")} ${preset.name}`}
                        onClick={() => setEditing(true)}>
                        <i className={"fas fa-pencil-alt"} />
                    </button>
                    <button
                        type={"button"}
                        className={"btn btn-sm btn-link text-danger FilterBar-manage-action"}
                        title={translate("delete_preset")}
                        aria-label={`${translate("delete_preset")} ${preset.name}`}
                        onClick={props.onDelete}>
                        <i className={"fas fa-trash-alt"} />
                    </button>
                </>
            ) : null}
        </div>
    );
};

type GroupProps = {
    source: "team" | "user";
    presets: IFilterPresetRuntime[];
    activePresetId: string | null;
    translate: Props["translate"];
    onReorder: Props["onReorder"];
    onRename: Props["onRename"];
    onDelete: Props["onDelete"];
    onToggleDefault: Props["onToggleDefault"];
};

/** A section of the sidebar: team or user presets. Non-editable rows render locked, everything else drags. */
const PresetGroup = (props: GroupProps) => {
    const { source, presets, translate } = props;
    const byId = new Map(presets.map((p) => [p.id, p]));

    const items: IOwSortableListItem[] = presets.map((p) => ({
        id: p.id,
        labelKey: p.name,
        hideToggle: true,
        isStatic: !p.editable,
    }));

    return (
        <div className={"FilterBar-section FilterBar-manage-group"} data-testid={`filterbar-manage-${source}`}>
            <div className={"FilterBar-section-title"}>
                {source === "team" ? <i className={"fas fa-users me-1"} /> : null}
                {translate(source === "team" ? "team_presets" : "my_presets")}
            </div>
            {presets.length === 0 ? (
                <div className={"FilterBar-section-help"}>{translate("no_presets_yet")}</div>
            ) : (
                <OwSortableList
                    items={items}
                    onReorder={(ids) => props.onReorder(source, ids)}
                    renderItem={(item) => {
                        const preset = byId.get(item.id);

                        if (!preset) {
                            return null;
                        }

                        return (
                            <PresetRow
                                preset={preset}
                                isActive={preset.id === props.activePresetId}
                                translate={translate}
                                onRename={(name) => props.onRename(preset.id, name)}
                                onDelete={() => props.onDelete(preset.id)}
                                onToggleDefault={() => props.onToggleDefault(preset.id)}
                            />
                        );
                    }}
                />
            )}
        </div>
    );
};

/**
 * Right-side sidebar for organising presets: drag to reorder within the team and personal lists,
 * rename inline, set the default view, delete. Team presets are locked for non-admins.
 * Every change persists immediately through the FilterBar's onPresetsChange, like the tab actions.
 */
export const PresetManageSidebar = (props: Props) => {
    const { isOpen, onClose, translate, presets } = props;

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    const teamPresets = presets.filter((p) => p.source === "team");
    const userPresets = presets.filter((p) => p.source === "user");
    const groupProps = {
        activePresetId: props.activePresetId,
        translate,
        onReorder: props.onReorder,
        onRename: props.onRename,
        onDelete: props.onDelete,
        onToggleDefault: props.onToggleDefault,
    };

    return createPortal(
        <div className={"FilterBar-drawer-host"}>
            <OwSidebar position={"right"} width={"340px"} isOpen={true} onClose={onClose} disableScroll={true}>
                <div className={"FilterBar-drawer FilterBar-manage d-flex flex-column h-100"} data-testid={"filterbar-manage-sidebar"}>
                    <div className={"FilterBar-drawer-header d-flex align-items-start"}>
                        <div className={"flex-grow-1"}>
                            <h5 className={"mb-0"}>{translate("manage_presets")}</h5>
                            <small className={"text-muted"}>{translate("drag_items_to_reorder")}</small>
                        </div>
                        <button
                            type={"button"}
                            className={"btn btn-sm btn-link FilterBar-close"}
                            aria-label={translate("close")}
                            onClick={onClose}>
                            <i className={"fas fa-times"} />
                        </button>
                    </div>

                    <div className={"FilterBar-drawer-body flex-grow-1"}>
                        {teamPresets.length > 0 ? <PresetGroup source={"team"} presets={teamPresets} {...groupProps} /> : null}
                        <PresetGroup source={"user"} presets={userPresets} {...groupProps} />
                    </div>
                </div>
            </OwSidebar>
        </div>,
        document.body
    );
};
